// src/ui/theme/semanticTokens.js
// FUENTE UNICA de los tokens semanticos del editor de estilos (Capa 1).
//
// Cada token mapea un nombre semantico -> { cssVar, value }. El `value` es el
// DEFAULT actual del codigo (derivado de tokens.js, sin duplicar hex), de modo
// que los SCSS pueden migrar a `var(--edu-..., <valor-actual>)`: mientras nadie
// declare la variable, el navegador usa el fallback = valor de hoy => render
// IDENTICO. El editor (futuro) declarara estas variables en :root para aplicar
// overrides en tiempo real.
//
// Doc: design-system/styles-editor/LAYERS.md (seccion 4, tokenizacion).
import { brand, neutral, semantic, font, radius } from "./tokens";

// Tints de marca (fondos verdes suaves) presentes hardcodeados en la superficie
// del profesor (#f1faf5 / #e8f7ef) que aun no vivian en tokens.js.
const tint = { soft: "#F1FAF5", hover: "#E8F7EF" };

// token semantico -> { cssVar, value (default actual) }
export const SEMANTIC_TOKENS = {
  "color.primary": { cssVar: "--edu-color-primary", value: brand.green },
  "color.primaryDark": {
    cssVar: "--edu-color-primary-dark",
    value: brand.greenDark,
  },
  "color.surface": { cssVar: "--edu-color-surface", value: neutral.card },
  "color.canvas": { cssVar: "--edu-color-canvas", value: neutral.canvas },
  "color.border": { cssVar: "--edu-color-border", value: neutral.border },
  "color.text": { cssVar: "--edu-color-text", value: neutral.text },
  "color.textMuted": {
    cssVar: "--edu-color-text-muted",
    value: neutral.textMuted,
  },
  "color.tintSoft": { cssVar: "--edu-color-tint-soft", value: tint.soft },
  "color.tintHover": { cssVar: "--edu-color-tint-hover", value: tint.hover },
  "color.danger": { cssVar: "--edu-color-danger", value: semantic.danger },
  // Acentos por componente. Por defecto = primario (el espejo usa fallback a
  // --edu-color-primary), pero se pueden editar de forma INDEPENDIENTE para que
  // cambiar un elemento no recoloree el resto.
  "color.tabActive": { cssVar: "--edu-color-tab-active", value: brand.green },
  "color.cardAccent": {
    cssVar: "--edu-color-card-accent",
    value: brand.green,
  },
  "color.chipActive": {
    cssVar: "--edu-color-chip-active",
    value: brand.green,
  },
  "color.tileRing": { cssVar: "--edu-color-tile-ring", value: brand.green },
  "color.itinAccent": {
    cssVar: "--edu-color-itin-accent",
    value: brand.green,
  },
  "color.modalAccent": {
    cssVar: "--edu-color-modal-accent",
    value: brand.green,
  },
  "color.tableAccent": {
    cssVar: "--edu-color-table-accent",
    value: brand.green,
  },
  "color.panelBar": {
    cssVar: "--edu-color-panel-bar",
    value: brand.green,
  },
  "color.wizardAccent": {
    cssVar: "--edu-color-wizard-accent",
    value: brand.green,
  },
  "color.confirmAccent": {
    cssVar: "--edu-color-confirm-accent",
    value: brand.green,
  },
  "color.sidebarBg": {
    cssVar: "--edu-color-sidebar-bg",
    value: neutral.sidebar,
  },
  // Menu lateral (items del Menu AntD en modo dark).
  "sidebar.itemText": {
    cssVar: "--edu-sidebar-item-text",
    value: "rgba(255,255,255,0.70)",
  },
  "sidebar.itemActiveBg": {
    cssVar: "--edu-sidebar-item-active-bg",
    value: "rgba(0,167,90,0.18)",
  },
  "sidebar.itemActiveText": {
    cssVar: "--edu-sidebar-item-active-text",
    value: "#FFFFFF",
  },
  "sidebar.itemHoverBg": {
    cssVar: "--edu-sidebar-item-hover-bg",
    value: "rgba(0,167,90,0.12)",
  },
  "sidebar.itemRadius": {
    cssVar: "--edu-sidebar-item-radius",
    value: "12px",
  },
  // Tipografia de los nombres del menu (independiente del texto general).
  "sidebar.itemFont": {
    cssVar: "--edu-sidebar-item-font",
    value: font.body,
  },
  "sidebar.itemWeight": {
    cssVar: "--edu-sidebar-item-weight",
    value: "400",
  },
  "sidebar.itemStyle": {
    cssVar: "--edu-sidebar-item-style",
    value: "normal",
  },
  "font.display": { cssVar: "--edu-font-display", value: font.display },
  "font.displayWeight": {
    cssVar: "--edu-font-display-weight",
    value: "800",
  },
  "font.displayStyle": {
    cssVar: "--edu-font-display-style",
    value: "normal",
  },
  "font.displayScale": {
    cssVar: "--edu-font-display-scale",
    value: "1",
  },
  "font.body": { cssVar: "--edu-font-body", value: font.body },
  "font.bodyWeight": {
    cssVar: "--edu-font-body-weight",
    value: "400",
  },
  "font.bodyStyle": {
    cssVar: "--edu-font-body-style",
    value: "normal",
  },
  "font.bodyScale": {
    cssVar: "--edu-font-body-scale",
    value: "1",
  },
  "radius.control": {
    cssVar: "--edu-radius-control",
    value: `${radius.control}px`,
  },
  "radius.card": { cssVar: "--edu-radius-card", value: `${radius.card}px` },
  // Avatar de la card de asistente (forma + tamano).
  "radius.avatarShape": { cssVar: "--edu-avatar-shape", value: "50%" },
  "radius.avatarSize": { cssVar: "--edu-avatar-size", value: "64px" },

  // ===========================================================================
  // CAPA 2 — Portal del Alumno (F2). Tokens ADITIVOS: no los consume ninguna
  // pantalla del profesor, no aparecen en el editor del profesor (la anatomia
  // los ignora) y no rompen el test (anatomy-driven). Valores iniciales =
  // mockups validados. Ver design-system/student-portal/STYLE_GUIDE_STUDENT.md.
  // ===========================================================================

  // Acento de marca (amarillo): ribbon "Ahora", tag "Mejor", borde instruccion.
  // Faltaba como token semantico aunque brand.yellow ya existe en tokens.js.
  "color.accent": { cssVar: "--edu-color-accent", value: brand.yellow },

  // Barra superior del portal alumno (AdminLayoutTop, distinta del sidebar).
  "color.studentHeaderBg": {
    cssVar: "--edu-color-student-header-bg",
    value: "#151A24",
  },

  // Tema VR / oscuro (modo gafas). El CONTRASTE BASE es fijo (no editable en el
  // editor) por seguridad visual en el visor; solo vrPrimary hereda lo editable.
  "color.vrBg": { cssVar: "--edu-color-vr-bg", value: neutral.sidebar },
  "color.vrSurface": { cssVar: "--edu-color-vr-surface", value: "#1B1F2A" },
  "color.vrText": { cssVar: "--edu-color-vr-text", value: "#FFFFFF" },
  "color.vrTextMuted": {
    cssVar: "--edu-color-vr-text-muted",
    value: "rgba(255,255,255,0.70)",
  },
  "color.vrPrimary": { cssVar: "--edu-color-vr-primary", value: brand.green },
  // Control VR: targets grandes para el puntero del mando.
  "radius.vrControl": { cssVar: "--edu-radius-vr-control", value: "16px" },
};

// Mapa { "--edu-...": valorDefault } para inyectar en :root (ThemeRuntimeProvider).
export function defaultCssVars() {
  return Object.fromEntries(
    Object.values(SEMANTIC_TOKENS).map(({ cssVar, value }) => [cssVar, value]),
  );
}

// Helper de consumo en JS: `cssVarRef("color.primary")` -> "var(--edu-color-primary, #00A75A)".
export function cssVarRef(token) {
  const def = SEMANTIC_TOKENS[token];
  if (!def) return undefined;
  return `var(${def.cssVar}, ${def.value})`;
}

export default SEMANTIC_TOKENS;
